import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FocusTrap } from "@/components/a11y/FocusTrap";
import { useAuthStore } from "@/stores/authStore";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
}

export function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const tier = useAuthStore((s) => s.user?.tier ?? "free");
  const maxTier = useAuthStore((s) => s.user?.maxTier ?? "");
  const currency = "€";
  const [maxToggle, setMaxToggle] = useState<"5x" | "20x">("5x");
  const [stubTier, setStubTier] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      dialogRef.current?.focus();
      setStubTier(null);
    }
  }, [open]);

  if (!open) return null;

  const currentTierKey = tier === "max" ? `max_${maxTier || "5x"}` : tier || "free";

  return (
    <FocusTrap>
      <div className="cx-shortcuts-backdrop animate-cx-fade-in" onClick={onClose}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-label={t("upgrade.modalTitle")}
          tabIndex={-1}
          className="cx-upgrade-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="cx-dialog-header flex items-center justify-between px-5 py-4">
            <h2 className="cx-dialog-title text-[15px] font-medium">
              {t("upgrade.modalTitle")}
            </h2>
            <button onClick={onClose} className="cx-msg-action" aria-label={t("common.close")}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {stubTier ? (
            <div className="cx-upgrade-stub">
              <p>{t("upgrade.stubMessage")}</p>
              <button className="cx-toggle-btn" onClick={() => setStubTier(null)}>
                {t("upgrade.notNow")}
              </button>
            </div>
          ) : (
            <>
              <div className="cx-upgrade-columns">
                {/* Free */}
                <PlanColumn
                  name={t("upgrade.free.name")}
                  price={t("upgrade.free.price")}
                  currency=""
                  period=""
                  questions={t("upgrade.free.questions")}
                  lookups=""
                  examples={[t("upgrade.free.q1"), t("upgrade.free.q2"), t("upgrade.free.q3")]}
                  isCurrent={currentTierKey === "free"}
                  onUpgrade={() => setStubTier("pro")}
                />

                {/* Pro */}
                <PlanColumn
                  name={t("upgrade.pro.name")}
                  price={t("upgrade.pro.price")}
                  currency={currency}
                  period={t("upgrade.pro.period")}
                  questions={t("upgrade.pro.questions")}
                  lookups={t("upgrade.pro.lookups")}
                  examples={[t("upgrade.pro.q1"), t("upgrade.pro.q2"), t("upgrade.pro.q3")]}
                  isCurrent={currentTierKey === "pro"}
                  onUpgrade={() => setStubTier("pro")}
                />

                {/* Max */}
                <div className={`cx-upgrade-column${currentTierKey.startsWith("max") ? " cx-upgrade-column--current" : ""}`}>
                  <span className="cx-upgrade-plan-name">{t("upgrade.max.name")}</span>

                  <div className="cx-upgrade-price">
                    <span className="cx-upgrade-price-amount">
                      {maxToggle === "5x" ? t("upgrade.max.price5x") : t("upgrade.max.price20x")} {currency}
                    </span>
                    <span className="cx-upgrade-price-period">{t("upgrade.max.period")}</span>
                  </div>

                  <div className="cx-upgrade-max-toggle">
                    <button
                      className={`cx-toggle-btn${maxToggle === "5x" ? " cx-toggle-btn--active" : ""}`}
                      onClick={() => setMaxToggle("5x")}
                    >
                      5×
                    </button>
                    <button
                      className={`cx-toggle-btn${maxToggle === "20x" ? " cx-toggle-btn--active" : ""}`}
                      onClick={() => setMaxToggle("20x")}
                    >
                      20×
                    </button>
                  </div>

                  <span className="cx-upgrade-limit">
                    {maxToggle === "5x" ? t("upgrade.max.questions5x") : t("upgrade.max.questions20x")}
                  </span>
                  <span className="cx-upgrade-limit">
                    {maxToggle === "5x" ? t("upgrade.max.lookups5x") : t("upgrade.max.lookups20x")}
                  </span>

                  <ul className="cx-upgrade-questions">
                    <li className="cx-upgrade-question">{t("upgrade.max.q1")}</li>
                    <li className="cx-upgrade-question">{t("upgrade.max.q2")}</li>
                    <li className="cx-upgrade-question">{t("upgrade.max.q3")}</li>
                  </ul>

                  {currentTierKey.startsWith("max") ? (
                    <button className="cx-upgrade-btn cx-upgrade-btn--current" disabled>
                      {t("upgrade.currentPlan")}
                    </button>
                  ) : (
                    <button
                      className="cx-upgrade-btn cx-upgrade-btn--upgrade"
                      onClick={() => setStubTier("max")}
                    >
                      {t("upgrade.upgrade")}
                    </button>
                  )}
                </div>
              </div>

              <p className="cx-upgrade-footnote">{t("upgrade.allPlansNote")}</p>
            </>
          )}
        </div>
      </div>
    </FocusTrap>
  );
}

function PlanColumn({
  name,
  price,
  currency,
  period,
  questions,
  lookups,
  examples,
  isCurrent,
  onUpgrade,
}: {
  name: string;
  price: string;
  currency: string;
  period: string;
  questions: string;
  lookups: string;
  examples: string[];
  isCurrent: boolean;
  onUpgrade: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={`cx-upgrade-column${isCurrent ? " cx-upgrade-column--current" : ""}`}>
      <span className="cx-upgrade-plan-name">{name}</span>

      <div className="cx-upgrade-price">
        <span className="cx-upgrade-price-amount">{price}{currency ? ` ${currency}` : ""}</span>
        {period && <span className="cx-upgrade-price-period">{period}</span>}
      </div>

      <span className="cx-upgrade-limit">{questions}</span>
      {lookups && <span className="cx-upgrade-limit">{lookups}</span>}

      <ul className="cx-upgrade-questions">
        {examples.map((q, i) => (
          <li key={i} className="cx-upgrade-question">{q}</li>
        ))}
      </ul>

      {isCurrent ? (
        <button className="cx-upgrade-btn cx-upgrade-btn--current" disabled>
          {t("upgrade.currentPlan")}
        </button>
      ) : (
        <button className="cx-upgrade-btn cx-upgrade-btn--upgrade" onClick={onUpgrade}>
          {t("upgrade.upgrade")}
        </button>
      )}
    </div>
  );
}
